import React, { useState, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import PropTypes from "prop-types";

import usePermissions from "hooks/usePermissions";
import useToken from "hooks/useToken";
import Cookies from "js-cookie";
import { useStateMachine } from "little-state-machine";
import { clearFiltersFromLocalStorage } from "helpers/localStorage";
import { Badge } from "react-bootstrap";
import { IItemProps } from "components/molecules/Navigation/interface/sidebar";
import { resetState, persistPage } from "store/store";
import { unsubscribeFromPushManager } from "helpers/notificationHelpers";
import NavContext from "./NavContext";

interface IMenuItemProps {
  item: IItemProps;
  closeNav: () => void;
  testId: string;
}

const MenuItem = function ({ item, closeNav, testId }: IMenuItemProps) {
  const [onHover, setOnHover] = useState(false);
  const { logout } = useAuth0();
  const { clearToken } = useToken();
  const { clearPermissions } = usePermissions();
  const location = useLocation();
  const { navigationConfig } = useContext(NavContext);
  const { isMobile, expanded } = navigationConfig;
  const { actions } = useStateMachine({ persistPage, resetState });

  // Check if menuItem is active
  let active = false;
  if (window.location.href.indexOf(item.parentUrl) > -1) {
    if (location.pathname === "/" && item.parentUrl === "/") active = true;
    if (item.parentUrl !== "/") active = true;
  }

  const handleOnClick = () => {
    setTimeout(() => {
      setOnHover(false);
    }, 100);

    actions.persistPage({
      page: 1,
    });

    if (item.id === "logout") {
      unsubscribeFromPushManager();
      clearToken();
      clearPermissions();
      clearFiltersFromLocalStorage();
      Cookies.remove("cargobase_logged_in_as_token");
      actions.resetState();
      logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    }
    if (isMobile) closeNav();
  };

  if (item.enabled === false) return null;

  return (
    <div className="position-relative">
      <Link
        to={item.to}
        onClick={handleOnClick}
        className={`navigation-link ${active ? "text-white" : "text-primary"} text-decoration-none pt-1.5 pb-1.5 font-b3`}
        data-testid={testId}
        tabIndex={0}
      >
        <li
          onMouseEnter={() => setOnHover(true)}
          onMouseLeave={() => setOnHover(false)}
          className={`navigation-item ${active && "bg-dark-active"}`}
        >
          <div className={`align-items-center justify-content-center d-flex ${active ? "text-white" : "text-primary"}`}>
            {item.icon}
          </div>
          {expanded && <span className="navigation-item-label">{item.label}</span>}
          {!!item.unreadCount && (
            <div className={`navigation-badge ${item.shouldBlink ? "blink" : ""}`}>
              <Badge variant={item.shouldBlink ? "danger" : "primary"} style={{ borderRadius: "50%" }}>
                {item.unreadCount >= 100 ? "99+" : item.unreadCount}
              </Badge>
            </div>
          )}
        </li>
      </Link>
      {!expanded && (
        <div
          style={{
            transition: "all .2s ease",
            opacity: !onHover ? "0%" : "100%",
            transform: onHover ? "translateX(15%)" : "translateX(-20%)",
            visibility: onHover ? "visible" : "hidden",
            zIndex: 999,
          }}
          className="navigation-side-label"
        >
          {item.label}
        </div>
      )}
    </div>
  );
};

MenuItem.propTypes = {
  item: PropTypes.instanceOf(Object).isRequired,
  closeNav: PropTypes.func.isRequired,
  testId: PropTypes.string.isRequired,
};

export default MenuItem;
